// «Мой кабинет» — то, что видит сам контрибьютор (0004): его часы, качество,
// заработок по его ставке, дни и записи с причинами отказов. Никаких чужих
// данных на этом экране нет by design — бэкенд отдаёт /my/stats только по
// contributor-сессии и только про самого человека.
import { useEffect, useState } from 'preact/hooks';
import { api, type DatoricMyStatsRes } from '../api';

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
const fmtDur = (sec: number | null): string => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};
const fmtDateTime = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const REVIEW_LABEL: Record<string, string> = {
  approved: 'принято',
  pending: 'на проверке',
  rejected: 'отклонено',
};

export function MySpacePage() {
  const [data, setData] = useState<DatoricMyStatsRes | null | 'error'>(null);
  const [tab, setTab] = useState<'all' | 'rejected'>('all');

  useEffect(() => {
    api.myStats().then(setData).catch(() => setData('error'));
  }, []);

  if (data === 'error') return <div class="empty">Статистика сейчас недоступна — стоит зайти чуть позже.</div>;
  if (!data) return <div class="empty">Загружаем…</div>;

  const m = data.me;
  const rejected = data.recent.filter((r) => r.reviewStatus === 'rejected');
  const list = tab === 'rejected' ? rejected : data.recent;
  const days = [...data.daily].reverse();
  const maxDay = Math.max(1, ...days.map((d) => d.uploadedHours));

  return (
    <>
      <div class="card-head">
        <div>
          <h2>{m.name ?? m.email}</h2>
          <div class="muted">{m.name ? m.email : 'моя статистика'}</div>
        </div>
      </div>

      <div class="tiles">
        <div class="tile"><div class="n">{fmtH(m.acceptedHours)} ч</div><div class="t">принято</div></div>
        <div class="tile"><div class="n">{fmtH(m.pendingHours)} ч</div><div class="t">на проверке</div></div>
        <div class="tile"><div class="n">{fmtH(m.rejectedHours)} ч</div><div class="t">брак</div></div>
        <div class="tile"><div class="n">{m.qualityScore ?? '—'}</div><div class="t">качество</div></div>
        <div class="tile">
          <div class="n">{fmtMoney(m.earnedCents)}</div>
          <div class="t">
            {m.rateCents == null ? 'ставка уточняется' : `заработано по $${(m.rateCents / 100).toFixed(2)}/ч`}
          </div>
        </div>
      </div>

      {m.pendingHours > 0 && (
        <div class="strip warn">
          {fmtH(m.pendingHours)} ч ещё на проверке — они добавятся к принятым после ревью Datoric.
        </div>
      )}

      {days.length > 0 && (
        <div class="panel">
          <h3>По дням</h3>
          <div class="bars">
            {days.map((d) => (
              <div class="bar-col" key={d.day} title={`${d.day}: ${fmtH(d.uploadedHours)} ч`}>
                <div class="bar-stack" style={{ height: `${(d.uploadedHours / maxDay) * 100}%` }}>
                  <div class="bar-rej" style={{ flex: d.rejectedHours }} />
                  <div class="bar-pend" style={{ flex: d.pendingHours }} />
                  <div class="bar-acc" style={{ flex: d.acceptedHours }} />
                </div>
                <div class="bar-label">{d.day.slice(8)}</div>
              </div>
            ))}
          </div>
          <div class="legend">
            <span><i class="sw acc" /> принято</span>
            <span><i class="sw pend" /> на проверке</span>
            <span><i class="sw rej" /> брак</span>
          </div>
        </div>
      )}

      <div class="panel">
        <div class="panel-head">
          <h3>Мои записи</h3>
          <div class="tabs">
            <button class={tab === 'all' ? 'tab on' : 'tab'} onClick={() => setTab('all')}>
              Все ({data.recent.length})
            </button>
            <button class={tab === 'rejected' ? 'tab on' : 'tab'} onClick={() => setTab('rejected')}>
              Отклонённые ({rejected.length})
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div class="empty">
            {tab === 'rejected' ? 'Отклонённых записей нет.' : 'Записей пока нет — они появятся после первой съёмки.'}
          </div>
        ) : (
          <div class="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th>Снято</th>
                  <th>Длина</th>
                  <th>Статус</th>
                  <th>Качество</th>
                  <th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{r.fileName ?? <span class="muted">без имени</span>}</td>
                    <td>{fmtDateTime(r.recordedAt ?? r.uploadedAt)}</td>
                    <td>{fmtDur(r.durationSeconds)}</td>
                    <td>
                      <span class={`chip ${r.reviewStatus === 'approved' ? 'active' : r.reviewStatus === 'rejected' ? 'fraud' : 'pend'}`}>
                        {REVIEW_LABEL[r.reviewStatus ?? ''] ?? r.reviewStatus ?? '—'}
                      </span>
                      {r.pipelineStatus === 'failed' && <span class="chip fraud">сбой обработки</span>}
                    </td>
                    <td>{r.quality ?? '—'}</td>
                    <td class="reason">{r.rejectionReason ?? <span class="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
